import { FC } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Text,
} from '@eluve/components';
import { DbEnums } from '@eluve/graphql-types';

import { LifecycleStatus, getLifecycleStatusLabel } from './lifecycle-status';

type LifecycleConfirmDialogProps = {
  open: boolean;
  /** The status being moved to; null when no confirmation is pending. */
  target: LifecycleStatus | null;
  /** Identifies the user in the prompt (e.g. their email). */
  subjectLabel?: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Confirmation prompt for the two Churned directions (ENG-4694): moving a user
 * *into* Churned and *out of* Churned (un-churn). Only opened by the owner for
 * those transitions; other transitions apply without a prompt.
 */
export const LifecycleConfirmDialog: FC<LifecycleConfirmDialogProps> = ({
  open,
  target,
  subjectLabel,
  isPending,
  onConfirm,
  onCancel,
}) => {
  const isIntoChurn = target === DbEnums.TenantUserLifecycleStatus.CHURNED;
  const subject = subjectLabel ? ` (${subjectLabel})` : '';
  const targetLabel = target ? getLifecycleStatusLabel(target) : '';

  return (
    <Dialog open={open}>
      <DialogContent size="lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Are you sure?</DialogTitle>
        </DialogHeader>
        <Text color="secondary">
          {isIntoChurn
            ? `This will mark the user${subject} as Churned.`
            : `This will move the user${subject} out of Churned and back to ${targetLabel}.`}
        </Text>
        <DialogFooter>
          <Button variant="outline" disabled={isPending} onClick={onCancel}>
            Cancel
          </Button>
          <Button loading={isPending} onClick={onConfirm}>
            {isIntoChurn ? 'Mark as Churned' : 'Un-churn'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
